// A tap that the browser did not turn into a click is still a press.
//
// On an iPhone, Safari decides from the touch whether to send a click, and a
// touch handler left on the whole window by the board — or anything else it
// judges the touch to belong to — makes it send none: the button is seen to
// press and nothing happens. "Undo does not respond." On a screen with a
// board, a finger that goes down and up on a button without moving, and gets
// no click from the browser within half a second, presses the button here
// instead — once — and says so to the server, which is how we learn it still
// happens and where. A click that turns up after that is not a second press.

import { reportFault } from "./api.ts";

/** How long a click may take to follow the finger lifting. */
const CLICK_WITHIN_MS = 500;
/** A finger that moved further than this was scrolling, not pressing. */
const STILL_PX = 10;

export function keepTaps(): void {
  let down: { button: HTMLButtonElement; x: number; y: number } | null = null;
  let waiting: { button: HTMLButtonElement; timer: number } | null = null;
  let pressedHere: { button: HTMLButtonElement; at: number } | null = null;

  const buttonOf = (target: EventTarget | null): HTMLButtonElement | null => {
    const el = target instanceof Element ? target.closest("button") : null;
    return el instanceof HTMLButtonElement && el.closest(".board-screen") ? el : null;
  };

  document.addEventListener(
    "pointerdown",
    (e) => {
      if (e.pointerType !== "touch") return;
      const button = buttonOf(e.target);
      down = button ? { button, x: e.clientX, y: e.clientY } : null;
    },
    true
  );

  document.addEventListener(
    "pointerup",
    (e) => {
      if (e.pointerType !== "touch" || !down) return;
      const { button, x, y } = down;
      down = null;
      if (buttonOf(e.target) !== button || Math.hypot(e.clientX - x, e.clientY - y) > STILL_PX) return;
      if (waiting) clearTimeout(waiting.timer);
      waiting = {
        button,
        timer: window.setTimeout(() => {
          waiting = null;
          if (!button.isConnected || button.disabled) return;
          reportFault("tap-without-click", (button.getAttribute("aria-label") || button.textContent || "").trim().slice(0, 60), {
            source: "press",
            screen: location.pathname,
          });
          pressedHere = { button, at: Date.now() };
          button.click();
        }, CLICK_WITHIN_MS),
      };
    },
    true
  );

  document.addEventListener(
    "click",
    (e) => {
      const button = buttonOf(e.target);
      if (!button) return;
      // The browser's own click, in time: nothing for us to do.
      if (waiting && waiting.button === button && e.isTrusted) {
        clearTimeout(waiting.timer);
        waiting = null;
        return;
      }
      // The browser's click arriving after we pressed the button ourselves:
      // the same press, not another.
      if (pressedHere && pressedHere.button === button && e.isTrusted && Date.now() - pressedHere.at < 1500) {
        pressedHere = null;
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true
  );
}
